/**
 * usePresenceHeartbeat.js
 * Mount once inside AppLayout. Pings the backend every 30s with the current route
 * so admins can see who is online and which page each user is on right now.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendHeartbeat } from '../api/presenceService';
import { NAV_SECTIONS } from '../config/navConfig';

const HEARTBEAT_MS = 30_000;

// Build a static path → label map from the same nav config the sidebar uses.
const PATH_TO_TITLE = NAV_SECTIONS.reduce((acc, section) => {
  section.items.forEach((it) => { acc[it.path] = it.label; });
  return acc;
}, {});

const titleFor = (pathname) => {
  if (PATH_TO_TITLE[pathname]) return PATH_TO_TITLE[pathname];
  const match = Object.keys(PATH_TO_TITLE).find((p) => pathname.startsWith(p) && p !== '/');
  return match ? PATH_TO_TITLE[match] : pathname;
};

export default function usePresenceHeartbeat() {
  const { user, token } = useAuth();
  const location = useLocation();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!user || !token) return undefined;

    const ping = () => {
      if (document.hidden) return; // save server load when tab isn't visible
      sendHeartbeat({
        currentPage: location.pathname,
        pageTitle:   titleFor(location.pathname),
      }).catch(() => { /* swallow — heartbeat must never block UX */ });
    };

    ping();
    timerRef.current = setInterval(ping, HEARTBEAT_MS);

    // Re-ping when tab becomes visible again so the admin view stays fresh.
    const onVisible = () => { if (!document.hidden) ping(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, token, location.pathname]);
}
